import React, { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Typography } from "@mui/material";

import { rrdCampaignService } from "../services/rrdCampaignService";
import { BRAND_CONST, HTTP_STATUS } from "@/config/constants";
import { logger } from "@/helpers/loggerHelper";
import { GlobalContext, LoaderContext } from "@/helpers/contexts/contexts";
import RRDJoinConfirmation from "../components/confirmation/RRDJoinConfirmation";
import PageLayout from "@/components/layouts/page/PageLayout";
import NoDataFound from "@/components/NoDataFound";
import { getDashboardPath } from "@/helpers/utils";

type RRDRecord = Record<string, any>;

function DirectMailRRDConfirmationPage() {
  const { selectedBrand, loggedInUser } = useContext(
    GlobalContext
  ) as RRDRecord;

  const navigate = useNavigate();

  const { state } = useLocation() as { state?: RRDRecord | null };
  const campaignId = state?.campaignId;

  const loader = useContext(LoaderContext) as (_loading: boolean) => void;

  const [campaignDrafttedData, setCampaignDrafttedData] =
    useState<RRDRecord | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchDraftData = useCallback(
    async (campaignId: any) => {
      try {
        setLoading(true);
        const data = {
          campaignId: campaignId,
          fkParticipantId: loggedInUser?.participantData?.id,
        };
        const res = await rrdCampaignService.getDraftRRDCampaign(data);
        if (res.status === HTTP_STATUS.OK) {
          const data = res.data;
          if (String(data?.campaignId) === String(campaignId)) {
            setCampaignDrafttedData(data);
          }
        }
      } catch (err) {
        logger.error("Fetch draftted data error", err);
      } finally {
        setLoading(false);
      }
    },
    [loader, loggedInUser?.participantData?.id]
  );

  useEffect(() => {
    if (campaignId) {
      fetchDraftData(campaignId);
    }
  }, [fetchDraftData, campaignId]);

  useEffect(() => {
    loader(loading);
  }, [loading, loader]);

  if (
    selectedBrand?.id === BRAND_CONST.MS_BRAND_ID ||
    selectedBrand?.id === BRAND_CONST.KUMON_BRAND_ID
  ) {
    // This feature is not available for this brand MS and Kumon
    navigate(`${getDashboardPath()}`, {
      replace: true,
    });
    return null;
  }

  if (!state || !campaignId) {
    navigate(`${getDashboardPath()}`, {
      replace: true,
    });
    return null;
  }

  const renderContent = () => {
    if (loading) {
      return <NoDataFound message="Loading..." />;
    }
    if (
      !campaignDrafttedData ||
      String(campaignDrafttedData?.campaignId) !== String(campaignId)
    ) {
      return <NoDataFound message={`No campaign found!`} />;
    }

    if (!campaignDrafttedData?.orderIds?.length) {
      return <NoDataFound message={`No order found!`} />;
    }

    return (
      <>
        <Typography
          component="h1"
          variant="h5"
          color="text.primary"
          gutterBottom
        >
          Confirmation
        </Typography>
        <Typography variant="caption" className="mb-5">
          Review your selections and make changes if required before you go to
          payment.
        </Typography>
        <RRDJoinConfirmation campaign={campaignDrafttedData} />
      </>
    );
  };

  return (
    <PageLayout className="join-campaign-page postcard-container pt-1">
      {renderContent()}
    </PageLayout>
  );
}

export default DirectMailRRDConfirmationPage;
